import * as Sentry from '@sentry/react';
import { GlobalSection, Website, WebsiteData } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import { getFormIdsInWebsiteData } from '@superfunnel/utils';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';
import { db } from '../Firebase';
import { updateDynamicPageHasPendingPublishChanges } from '../services/DynamicPagesService';
import { publishGlobalSection } from '../services/GlobalSectionsService';
import {
  emptyConverter,
  groupBy,
  sendMessageToIframeByWebsiteId,
} from '../utils';
import { track } from '../utils/analytics';
import { LimitedStack } from '../utils/limited-stack';
import { useAuditLog } from './AuditLogContext';
import { UserContext } from './UserContext';
import { applyChange as applyChangeToWebsite } from './website-updater/apply-website-changes';

export function getItemFromSection(
  section: any,
  propKey?: string,
  subitems?: { id: string; propKey: string; type: 'array' }[] | undefined,
) {
  let itemFound: any = section;

  if (subitems?.length) {
    subitems.forEach((item) => {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
    });
    if (!itemFound) return;
  }

  if (!propKey) return itemFound;
  const propKeyParts = propKey.split('.');
  propKeyParts.forEach((propKeyPart) => {
    if (!itemFound) return;

    itemFound = itemFound[propKeyPart];
  });

  return itemFound;
}

export type WebsiteUpdateType =
  | 'text'
  | 'htmlElementChanged'
  | 'changeDesign'
  | 'addSectionAbove'
  | 'addSectionBelow'
  | 'moveSectionUp'
  | 'moveSectionDown'
  | 'reorderSections'
  | 'deleteSection'
  | 'duplicateSection'
  | 'deleteSectionItem'
  | 'editSectionItem'
  | 'duplicateSectionItem'
  | 'addSectionItem'
  | 'editButton'
  | 'editHtmlAction'
  | 'editSection'
  | 'editForm'
  | 'replaceForm'
  | 'editImageSettings'
  | 'selectImage'
  | 'selectThumbnail'
  | 'revertChange'
  | 'discard'
  | 'resetConfig'
  | 'resetBrand'
  | 'editBrand'
  | 'editConfig'
  | 'convertGlobalToLocal'
  | 'convertLocalToGlobal'
  | 'customValueMentionClick'
  // Popup update types
  | 'addPopup'
  | 'updatePopup'
  | 'deletePopup';

type UpdateWebsiteFn = (wu: WebsiteUpdate) => void;

type CreateUpdateWebsiteFn = (
  dynamicPageId: string,
  websiteId: string,
  sectionId: string,
  propKey: string,
  content: any,
  type: WebsiteUpdateType,
  subitems?: { id: string; propKey: string; type: 'array' }[],
  before?: WebsiteData,
) => WebsiteUpdate | undefined;

export type WebsiteUpdate = {
  id: string;
  accountId: string;
  campaignId: string;
  websiteId: string;
  sectionId: string;
  propKey: string;
  content: any;
  type: WebsiteUpdateType;
  subitems?: { id: string; propKey: string; type: 'array' }[];
  before?: WebsiteData;
};

export interface WebsiteUpdaterContextProps {
  saveStatus: 'none' | 'saving' | 'done';
  setSaveStatus: (_: 'none' | 'saving' | 'done') => void;
  lastSavedAt: Date | null;

  publishing: boolean;
  publish: (dynamicPageId: string, websiteId: string) => Promise<any>;

  discarding: boolean;
  discardChanges: (dynamicPageId: string, websiteId: string) => Promise<any>;

  createWebsiteUpdate: CreateUpdateWebsiteFn;
  updateWebsite: UpdateWebsiteFn;

  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: () => boolean;
  forwardRevert: () => boolean;
  clearHistory: () => void;

  applyChangesSync: (
    websiteId: string,
    development: WebsiteData,
    extra?: WebsiteUpdate[],
  ) => any;
}

export const WebsiteUpdaterContext = createContext<WebsiteUpdaterContextProps>({
  saveStatus: 'none',
  setSaveStatus: () => {},
  lastSavedAt: null,

  publishing: false,
  publish: async () => {},

  discarding: false,
  discardChanges: async () => {},

  updateWebsite: () => undefined,
  createWebsiteUpdate: () => undefined,

  canRevert: false,
  canForward: false,
  revertLatestUpdate: () => false,
  forwardRevert: () => false,
  clearHistory: () => {},

  applyChangesSync: () => {},
});

export function WebsiteUpdaterContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();
  const [saveStatus, setSaveStatus] = useState<'none' | 'saving' | 'done'>(
    'none',
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [updates, setUpdates] = useState<WebsiteUpdate[]>([]);
  const [history, setHistory] = useState<LimitedStack<WebsiteUpdate>>(
    new LimitedStack(20),
  );
  const [revertHistory, setRevertHistory] = useState<
    LimitedStack<WebsiteUpdate>
  >(new LimitedStack(20));
  const [publishGlobalSectionModalOpen, setPublishGlobalSectionModalOpen] =
    useState<{
      websiteId: string;
      globalSectionId: string;
      loading?: boolean;
    } | null>(null);

  const createWebsiteUpdate: CreateUpdateWebsiteFn = useCallback(
    (
      dynamicPageId,
      websiteId,
      sectionId,
      propKey,
      content,
      type,
      subitems,
      before,
    ) => {
      if (!selectedAccount) return;

      const wu: WebsiteUpdate = {
        id: uid(8),
        accountId: selectedAccount.id,
        campaignId: dynamicPageId,
        websiteId,
        sectionId,
        propKey,
        subitems,
        content,
        type,
        before,
      };

      return wu;
    },
    [selectedAccount],
  );

  const updateWebsite: UpdateWebsiteFn = useCallback(
    (wu: WebsiteUpdate) => {
      if (!selectedAccount) return;

      setSaveStatus('saving');
      setUpdates((u) => [...u, wu]);
      if (wu.before) {
        setHistory((h) => {
          const newHistory = new LimitedStack(h);
          newHistory.push(wu);
          return newHistory;
        });
      }
      setRevertHistory(new LimitedStack(20));
    },
    [selectedAccount],
  );

  const clearHistory = useCallback(() => {
    setHistory((history) => new LimitedStack(history.getMaxLength()));
    setRevertHistory(
      (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
    );
  }, []);

  // Use the extracted applyChange function directly
  const applyChange = useCallback(
    (wu: WebsiteUpdate, development: WebsiteData) => {
      return applyChangeToWebsite(wu, development);
    },
    [],
  );

  const applyChangesSync = useCallback(
    (websiteId: string, development: WebsiteData, extra?: WebsiteUpdate[]) => {
      const websiteUpdates = [...updates, ...(extra ?? [])].filter(
        (wu) => wu.websiteId === websiteId,
      );

      websiteUpdates.forEach((wu) => applyChange(wu, development));

      return development;
    },
    [applyChange, updates],
  );

  const revertLatestUpdate = useCallback(() => {
    if (!history.getSize()) return false;

    const newHistory = new LimitedStack(history);
    const changeToRevert = newHistory.pop();

    setHistory(newHistory);

    if (!changeToRevert?.before) return false;

    const revertWebsiteUpdate = createWebsiteUpdate(
      changeToRevert.campaignId,
      changeToRevert.websiteId,
      changeToRevert.sectionId,
      changeToRevert.propKey,
      changeToRevert.content,
      'revertChange',
      changeToRevert.subitems,
      changeToRevert.before,
    );

    if (!revertWebsiteUpdate) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, revertWebsiteUpdate]);
    setRevertHistory((rh) => {
      const newHistory = new LimitedStack(rh);
      newHistory.push({ ...changeToRevert, id: uid(8) });
      return newHistory;
    });

    const newDev = JSON.parse(JSON.stringify(changeToRevert.before));
    applyChange(revertWebsiteUpdate, newDev);
    sendMessageToIframeByWebsiteId(
      {
        eventName: 'websiteUpdated',
        website: newDev,
      },
      changeToRevert.websiteId,
    );

    return true;
  }, [history, createWebsiteUpdate, applyChange]);

  const forwardRevert = useCallback(() => {
    if (!revertHistory.getSize()) return false;

    const newHistory = new LimitedStack(revertHistory);
    const forwardChange = newHistory.pop();

    setRevertHistory(newHistory);

    if (!forwardChange) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, forwardChange]);
    setHistory((h) => {
      const newHistory = new LimitedStack(h);
      newHistory.push(forwardChange);
      return newHistory;
    });

    const newDev = JSON.parse(JSON.stringify(forwardChange.before));
    applyChange(forwardChange, newDev);
    sendMessageToIframeByWebsiteId(
      {
        eventName: 'websiteUpdated',
        website: newDev,
      },
      forwardChange.websiteId,
    );

    return true;
  }, [applyChange, revertHistory]);

  const _saveWebsite = useCallback(
    async (
      websiteId: string,
      websiteUpdates: WebsiteUpdate[],
      dynamicPageId: string,
    ) => {
      const websiteDoc = await getDoc(
        doc(
          db,
          'accounts',
          websiteUpdates[0].accountId,
          'campaigns',
          dynamicPageId,
          'websites',
          websiteId,
        ).withConverter(emptyConverter<Website>()),
      );

      const development = websiteDoc.data()?.development;
      if (!websiteDoc.exists() || !development) return { updated: false };

      let update = false;

      for (const wu of websiteUpdates) {
        update = applyChange(wu, development) || update;
      }

      if (update) {
        // Compute formIds from the updated development data
        development.formIds = Array.from(getFormIdsInWebsiteData(development));

        await updateDoc(websiteDoc.ref, {
          development: development as any,
        });

        // Mark the dynamic page as having unpublished changes
        await updateDynamicPageHasPendingPublishChanges(
          websiteUpdates[0].accountId,
          dynamicPageId,
          true,
        );
      }

      return { updated: update };
    },
    [applyChange],
  );

  const _saveGlobalSection = useCallback(
    async (websiteId: string, websiteUpdates: WebsiteUpdate[]) => {
      const globalSectionId = websiteId.replace('globalSectionId_', '');
      const globalSectionDoc = await getDoc(
        doc(
          db,
          'accounts',
          websiteUpdates[0].accountId,
          'globalSections',
          globalSectionId,
        ).withConverter(emptyConverter<GlobalSection>()),
      );

      const sectionDevelopment = globalSectionDoc.data()?.development;
      if (!globalSectionDoc.exists() || !sectionDevelopment)
        return { updated: false };

      let update = false;

      const simulatedDevelopment: WebsiteData = {
        sections: [sectionDevelopment],
      };

      for (const wu of websiteUpdates) {
        update = applyChange(wu, simulatedDevelopment);
      }

      if (update) {
        await updateDoc(globalSectionDoc.ref, {
          development: sectionDevelopment as any,
          changesToPublish: true,
        });
      }

      return { updated: update };
    },
    [applyChange],
  );

  useEffect(() => {
    const _updates = Object.values(updates);
    if (!_updates.length) return;

    const timer = setTimeout(async () => {
      const _updates = Object.values(updates);
      if (!_updates.length) return;

      setUpdates([]);

      const byDynamicPage = Object.entries(
        groupBy(_updates, (u) => u.campaignId),
      );

      for (const [dynamicPageId, campaignUpdates] of byDynamicPage) {
        const byWebsite = Object.entries(
          groupBy(campaignUpdates, (u) => u.websiteId),
        );
        const promises = byWebsite.map(async ([websiteId, websiteUpdates]) => {
          if (websiteId.startsWith('globalSectionId_')) {
            return _saveGlobalSection(websiteId, websiteUpdates);
          } else {
            return _saveWebsite(websiteId, websiteUpdates, dynamicPageId);
          }
        });

        await Promise.all(promises).finally(() => {
          setSaveStatus('done');
          setLastSavedAt(new Date());
        });
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [_saveGlobalSection, _saveWebsite, updates]);

  /**
   * @deprecated Discard is no longer supported for websites. Changes are auto-saved.
   * This function is kept for backwards compatibility but will throw an error.
   */
  const _discardChangesWebsite = useCallback(
    async (_dynamicPageId: string, _websiteId: string) => {
      throw new Error(
        'Discard is no longer supported for websites. Changes are auto-saved.',
      );
    },
    [],
  );

  const _discardChangesGlobalSection = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (!selectedAccount) return;
      if (discarding) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      setDiscarding(true);
      try {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        const globalSectionDoc = await getDoc(
          doc(
            db,
            'accounts',
            selectedAccount.id,
            'globalSections',
            globalSectionId,
          ).withConverter(emptyConverter<GlobalSection>()),
        );

        const development = globalSectionDoc.data()?.development;
        const production = globalSectionDoc.data()?.production;
        if (!globalSectionDoc.exists() || !production || !development) {
          throw new Error('This global section was not yet published');
        }

        const prodWebsiteData: WebsiteData = {
          sections: [production],
        };

        const developmentWebsiteData: WebsiteData = {
          sections: [development],
        };

        const discardWU = createWebsiteUpdate(
          dynamicPageId,
          websiteId,
          '',
          '',
          JSON.parse(JSON.stringify(prodWebsiteData)),
          'discard',
          undefined,
          developmentWebsiteData,
        );

        if (discardWU) {
          setHistory((h) => {
            const newHistory = new LimitedStack(h);
            newHistory.push(discardWU);

            return newHistory;
          });
        }
        setRevertHistory(
          (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
        );

        await updateDoc(globalSectionDoc.ref, {
          development: production as any,
          changesToPublish: false,
        });

        sendMessageToIframeByWebsiteId(
          {
            eventName: 'websiteUpdated',
            website: prodWebsiteData,
          },
          websiteId,
        );
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        throw e;
      } finally {
        setDiscarding(false);
      }
    },
    [selectedAccount, discarding, updates, createWebsiteUpdate],
  );

  const discardChanges = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        return _discardChangesGlobalSection(dynamicPageId, websiteId);
      } else {
        return _discardChangesWebsite(dynamicPageId, websiteId);
      }
    },
    [_discardChangesGlobalSection, _discardChangesWebsite],
  );

  const _publishGlobalSection = useCallback(
    async (websiteId: string, globalSectionId: string) => {
      if (!selectedAccount) return;
      if (publishing) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      auditLog.log(
        'update',
        'Publishing global section',
        'globalSection',
        globalSectionId,
        undefined,
        undefined,
        {},
      );

      track('publish_global_section', {
        globalSectionId,
        websiteId,
      });

      setPublishing(true);
      return publishGlobalSection(selectedAccount.id, globalSectionId).finally(
        () => {
          setPublishing(false);
        },
      );
    },
    [selectedAccount, publishing, updates, auditLog],
  );

  /**
   * Publish is now only used for GlobalSections.
   * Websites are auto-saved and no longer require publishing.
   */
  const publish = useCallback(
    async (_dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        setPublishGlobalSectionModalOpen({
          websiteId,
          globalSectionId,
          loading: false,
        });
        return;
      }
      // Websites are auto-saved, no publishing needed
    },
    [],
  );

  return (
    <WebsiteUpdaterContext.Provider
      value={{
        saveStatus,
        setSaveStatus,
        lastSavedAt,

        publishing,
        publish,

        discarding,
        discardChanges,

        createWebsiteUpdate,
        updateWebsite,

        canRevert: history.getSize() > 0,
        canForward: revertHistory.getSize() > 0,
        revertLatestUpdate,
        forwardRevert,
        clearHistory,

        applyChangesSync,
      }}
    >
      {props.children}
      <AlertModal
        open={publishGlobalSectionModalOpen !== null}
        onClose={() => setPublishGlobalSectionModalOpen(null)}
        title="Publish Global Section"
        description="Are you sure you want to publish this global section? This will update all websites that use this section."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setPublishGlobalSectionModalOpen(null),
            type: 'secondary',
          },
          {
            title: 'Publish',
            type: 'primary',
            color: 'primary',
            loading: publishGlobalSectionModalOpen?.loading,
            onClick: () => {
              const { websiteId, globalSectionId } =
                publishGlobalSectionModalOpen || {};
              if (!websiteId || !globalSectionId) return;
              setPublishGlobalSectionModalOpen((prev) =>
                prev
                  ? {
                      ...prev,
                      loading: true,
                    }
                  : null,
              );
              _publishGlobalSection(websiteId, globalSectionId)
                .then(() => {
                  setPublishGlobalSectionModalOpen(null);
                })
                .catch((e) => {
                  Sentry.captureException(e);
                  console.error(e);
                  setPublishGlobalSectionModalOpen((prev) =>
                    prev
                      ? {
                          ...prev,
                          loading: false,
                        }
                      : null,
                  );
                });
            },
          },
        ]}
        type={'warn'}
      />
    </WebsiteUpdaterContext.Provider>
  );
}
